import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import SelectTemplate from '../components/meet/SelectTemplate';
import MeetDetails from '../components/meet/MeetDetails';
import ManualEventEntry from '../components/meet/ManualEventEntry';
import MeetSummary from '../components/meet/MeetSummary';
import PurchasePaywall from '../components/PurchasePaywall';
import { createMeet, updateMeet, getMeet, getPurchaseStatus } from '../utils/api';
import { getSettings } from '../utils/settings';
import {
  normalizeSportType,
  DEFAULT_POOL_LANES,
  DEFAULT_SWIM_PRESET,
  DEFAULT_POOL_UNIT,
  DEFAULT_SPORT_PROFILE_KEY,
} from '../utils/sportConfig';

// Cache key used to persist the in-progress wizard form across history
// navigations. Without this, hitting the back arrow from the live meet view
// would re-mount the wizard with empty state — the user would lose what
// they had typed in even though the URL says they're "on the Review step".
const WIZARD_CACHE_KEY = 'foresight_wizard_form';

// If the user successfully creates a meet from this wizard run and then
// navigates back to the Review step, we want the "Create Meet" button to
// turn into "Save Changes" and call updateMeet — otherwise we'd silently
// duplicate the meet on every re-tap.
const WIZARD_CREATED_ID_KEY = 'foresight_wizard_created_id';

const DEFAULT_FORM = {
  template: null,
  country: 'United States',
  name: '',
  venue: '',
  date: '',
  startTime: '',
  type: 'outdoor',
  level: '',
  // Multi-sport expansion (Feb 2026). 'track' (default), 'swim', 'xc'.
  // Persisted to MongoDB via /api/meets so the spectator view can pick
  // the right vocabulary (Event/Heat vs Division/Race) and feature flags
  // (pool lanes, hide field events) without an extra round trip.
  sport_type: 'track',
  // Only used when sport_type === 'swim'. Defaults to null otherwise so
  // older meets in MongoDB round-trip safely.
  pool_lanes: null,
  // Only used when sport_type === 'swim'. 'standard_hs' (~25-event HS
  // dual) or 'summer_club' (~30-event age-group club). Coaches pick
  // this on the MeetDetails screen alongside the lane count.
  swim_preset: 'standard_hs',
  // Only used when sport_type === 'swim'. 'yards' (SCY, default) or
  // 'meters'. Baked into the generated event names ('50y' vs '50m')
  // and flips the NFHS 500-free → 400-free swap for metric pools.
  pool_unit: 'yards',
  events: [],
};

/**
 * Build a fresh-form template that respects the user's
 * `defaultSportProfile` Settings preference. Falls back to track when
 * Settings hasn't been visited yet. Swim default seeds the wizard with
 * lane count + preset + pool unit so MeetDetails renders the pool
 * inputs immediately.
 */
function buildDefaultForm() {
  let sport = 'track';
  try {
    const s = getSettings();
    sport = normalizeSportType(s?.[DEFAULT_SPORT_PROFILE_KEY]);
  } catch (_) { /* localStorage unavailable — stay on track */ }
  if (sport === 'swim') {
    return {
      ...DEFAULT_FORM,
      sport_type: 'swim',
      pool_lanes: DEFAULT_POOL_LANES,
      swim_preset: DEFAULT_SWIM_PRESET,
      pool_unit: DEFAULT_POOL_UNIT,
    };
  }
  if (sport === 'xc') {
    return {
      ...DEFAULT_FORM,
      sport_type: 'xc',
      pool_lanes: null,
      swim_preset: null,
      pool_unit: null,
    };
  }
  return DEFAULT_FORM;
}

export default function CreateMeetFlow() {
  const navigate = useNavigate();
  const { meetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const editMode = searchParams.get('edit') === 'true';
  const [showPaywall, setShowPaywall] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Step is sourced from the URL so each step is a real history entry.
  // That way, the device back gesture (and our in-app back arrow) walks the
  // wizard one step at a time instead of skipping all the way out.
  const stepFromUrl = parseInt(searchParams.get('step') || '', 10);
  const step = !isNaN(stepFromUrl) ? stepFromUrl : (editMode ? 2 : 1);

  // Check if user can create a meet (skip for edit mode)
  useEffect(() => {
    const checkPurchaseStatus = async () => {
      if (editMode) return;

      try {
        const status = await getPurchaseStatus();
        if (!status.is_premium && status.free_meets_remaining <= 0) {
          setShowPaywall(true);
        }
      } catch (error) {
        console.error('Failed to check purchase status:', error);
      }
    };

    checkPurchaseStatus();
  }, [editMode]);

  // Hydrate the form from sessionStorage so revisiting an earlier step (via
  // back) keeps everything the user already typed. We intentionally do NOT
  // restore in editMode — the edit flow loads its own data from the meet
  // record below — and we also do NOT restore when the user entered the
  // wizard fresh (no ?step in URL). That's the signal that they hit
  // "Create New Meet" on the Dashboard, so they expect a blank slate even
  // if an earlier aborted flow left data in sessionStorage.
  const enteredFresh = !editMode && !searchParams.get('step');
  const [meetData, setMeetData] = useState(() => {
    if (editMode) return DEFAULT_FORM;
    // Fresh entry: respect the user's saved Default Sport Profile so a
    // coach who lives in swim or xc doesn't have to flip the sport
    // picker every meet.
    const fresh = buildDefaultForm();
    if (enteredFresh) {
      // Wipe stale wizard cache at initializer time so the very first render
      // is blank — no one-frame flash of the previous meet's values.
      try {
        sessionStorage.removeItem(WIZARD_CACHE_KEY);
        sessionStorage.removeItem(WIZARD_CREATED_ID_KEY);
      } catch (_) { /* ignore */ }
      return fresh;
    }
    try {
      const cached = sessionStorage.getItem(WIZARD_CACHE_KEY);
      if (cached) return { ...fresh, ...JSON.parse(cached) };
    } catch (_) { /* corrupt cache — fall through */ }
    return fresh;
  });

  // If we entered the wizard fresh (no ?step in URL, not editing) clear any
  // stale form state from a prior aborted flow and stamp the URL.
  useEffect(() => {
    if (editMode) return;
    if (!searchParams.get('step')) {
      // The useState lazy initializer has already seeded `meetData` with
      // `buildDefaultForm()` because `enteredFresh` was true. We just
      // need to clear the stale session cache (the lazy initializer
      // also did this, but only conditionally; running it again here is
      // idempotent + cheap) and stamp the URL with ?step=1 so the
      // browser-back gesture walks the wizard one step at a time.
      sessionStorage.removeItem(WIZARD_CACHE_KEY);
      sessionStorage.removeItem(WIZARD_CREATED_ID_KEY);
      setSearchParams({ step: '1' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist any change so the next history entry / re-mount can rehydrate.
  useEffect(() => {
    if (editMode) return;
    try {
      sessionStorage.setItem(WIZARD_CACHE_KEY, JSON.stringify(meetData));
    } catch (_) { /* quota — ignore */ }
  }, [meetData, editMode]);

  // Load existing meet data if editing
  useEffect(() => {
    const loadMeet = async () => {
      if (meetId && editMode) {
        try {
          const existingMeet = await getMeet(meetId);
          if (existingMeet) {
            setMeetData(existingMeet);
          }
        } catch (error) {
          console.error('Failed to load meet:', error);
          // Fallback to localStorage
          const meets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
          const localMeet = meets.find(m => String(m.id) === String(meetId));
          if (localMeet) {
            setMeetData(localMeet);
          }
        }
      }
    };
    
    loadMeet();
  }, [meetId, editMode]);

  const updateMeetData = (data) => {
    // If this is template data (has events and other properties), merge it fully
    if (data.isFromTemplate) {
      setMeetData(prev => ({
        ...prev,
        ...data,
        country: prev.country, // Keep country from default
      }));
    } else {
      setMeetData(prev => ({ ...prev, ...data }));
    }
  };

  // nextStep pushes a new history entry so the device back gesture walks
  // back through the wizard one step at a time. We always push (never
  // replace) so each step gets its own entry.
  const nextStep = () => {
    const target = step + 1;
    if (editMode) {
      navigate(`/edit-meet/${meetId}?edit=true&step=${target}`);
    } else {
      navigate(`/create-meet?step=${target}`);
    }
  };

  // prevStep relies on the browser history we built above — calling
  // navigate(-1) takes the user to whichever step they came from (or out of
  // the wizard entirely if they entered at step 2 via the Edit Meet button).
  const prevStep = () => navigate(-1);

  const handleComplete = async (finalData) => {
    if (isLoading) return;
    setIsLoading(true);

    // Only merge events from finalData if provided (to handle async state updates)
    const dataToSave = finalData?.events
      ? { ...meetData, events: finalData.events }
      : meetData;

    // If the wizard already produced a meet on a previous "Create Meet" tap
    // (and the user navigated back to Review via the back arrow), this tap
    // should patch that meet rather than create a second one.
    const previouslyCreatedId = sessionStorage.getItem(WIZARD_CREATED_ID_KEY);

    try {
      if (editMode && meetId) {
        // Update existing meet via API
        await updateMeet(meetId, {
          name: dataToSave.name,
          venue: dataToSave.venue,
          country: dataToSave.country,
          date: dataToSave.date,
          startTime: dataToSave.startTime,
          type: dataToSave.type,
          level: dataToSave.level,
          sport_type: dataToSave.sport_type,
          pool_lanes: dataToSave.sport_type === 'swim' ? dataToSave.pool_lanes : null,
          swim_preset: dataToSave.sport_type === 'swim' ? dataToSave.swim_preset : null,
          pool_unit: dataToSave.sport_type === 'swim' ? dataToSave.pool_unit : null,
          events: dataToSave.events
        });

        // After saving an edit, push the meet view onto history. The user
        // can step back through the wizard exactly as they came in (which
        // is how every other "back" in this app behaves).
        navigate(`/meet/${meetId}`);
      } else if (previouslyCreatedId) {
        // User came back to Review and re-tapped — update the existing meet
        // instead of creating a duplicate.
        await updateMeet(previouslyCreatedId, {
          name: dataToSave.name,
          venue: dataToSave.venue,
          country: dataToSave.country,
          date: dataToSave.date,
          startTime: dataToSave.startTime,
          type: dataToSave.type,
          level: dataToSave.level,
          sport_type: dataToSave.sport_type,
          pool_lanes: dataToSave.sport_type === 'swim' ? dataToSave.pool_lanes : null,
          swim_preset: dataToSave.sport_type === 'swim' ? dataToSave.swim_preset : null,
          pool_unit: dataToSave.sport_type === 'swim' ? dataToSave.pool_unit : null,
          events: dataToSave.events
        });
        toast.success('Meet updated');
        navigate(`/meet/${previouslyCreatedId}`);
      } else {
        // Create new meet via API
        const newMeet = await createMeet({
          name: dataToSave.name,
          venue: dataToSave.venue,
          country: dataToSave.country,
          date: dataToSave.date,
          startTime: dataToSave.startTime,
          type: dataToSave.type,
          level: dataToSave.level,
          sport_type: dataToSave.sport_type,
          pool_lanes: dataToSave.sport_type === 'swim' ? dataToSave.pool_lanes : null,
          swim_preset: dataToSave.sport_type === 'swim' ? dataToSave.swim_preset : null,
          pool_unit: dataToSave.sport_type === 'swim' ? dataToSave.pool_unit : null,
          events: dataToSave.events
        });

        // If createMeet had to fall back to local-only (network/auth issue),
        // surface that to the user instead of silently navigating — otherwise
        // they share a link that resolves to "Meet Not Found" for spectators.
        if (newMeet && newMeet._offline) {
          toast.error(
            "Meet saved locally — couldn't reach the cloud. Share link will not work until your device syncs.",
            { duration: 6000 }
          );
        }

        // Remember which meet this wizard run produced so a return-trip via
        // the back arrow patches instead of duplicating.
        try {
          sessionStorage.setItem(WIZARD_CREATED_ID_KEY, String(newMeet.id));
        } catch (_) { /* quota — ignore */ }

        // Push the meet view onto history (do NOT replace) so tapping back
        // walks the user back through Review → Configure → Details → Template.
        navigate(`/meet/${newMeet.id}`);
      }
    } catch (error) {
      console.error('Failed to save meet:', error);
      if (error.message === 'PAYWALL_REQUIRED') {
        setShowPaywall(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle paywall close - go back to dashboard if user doesn't purchase
  const handlePaywallClose = () => {
    setShowPaywall(false);
    navigate('/');
  };

  // Handle successful purchase
  const handlePurchaseComplete = async () => {
    // Refresh purchase status after purchase
    try {
      const status = await getPurchaseStatus();
      // Only hide paywall if user is now premium or has free meets
      if (status.is_premium || status.free_meets_remaining > 0) {
        setShowPaywall(false);
      }
    } catch (error) {
      console.error('Failed to refresh purchase status:', error);
      setShowPaywall(false); // Close anyway
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Purchase Paywall */}
      <PurchasePaywall 
        open={showPaywall}
        onClose={handlePaywallClose}
        onPurchaseComplete={handlePurchaseComplete}
      />
      {step === 1 && !editMode && (
        <SelectTemplate 
          onNext={nextStep}
          onSelect={(templateData) => {
            // Picking a template (or "Start Fresh") on step 1 means the
            // user is starting a brand new meet. Always drop the prior
            // wizard's created-meet id so saving creates a NEW meet
            // instead of patching the previous one. Repro: user creates
            // meet A, taps back to step 1 (URL still has ?step=1 so the
            // "enteredFresh" wipe at mount doesn't fire), picks Start
            // Fresh, fills in meet B, taps Save → without this line it
            // updates meet A's row. (See PRD Invariant #8.)
            try {
              sessionStorage.removeItem(WIZARD_CREATED_ID_KEY);
            } catch (_) { /* ignore */ }
            if (templateData) {
              // Merge template data directly into meetData (isFromTemplate flag included)
              updateMeetData({ ...templateData, isFromTemplate: true });
            } else {
              // "Start Fresh" — user explicitly chose a blank slate. Wipe
              // the entire form back to defaults rather than only flipping
              // the template flag. Otherwise stale name/venue/date/time
              // from a previously-saved wizard run leak into the new
              // meet's MeetDetails screen.
              setMeetData(DEFAULT_FORM);
            }
          }}
          onBack={() => navigate('/')}
        />
      )}
      
      {(step === 2 || (editMode && step === 2)) && (
        <MeetDetails 
          data={meetData}
          onUpdate={updateMeetData}
          onNext={nextStep}
          onBack={prevStep}
          isEditing={editMode}
        />
      )}
      
      {step === 3 && (
        <ManualEventEntry 
          data={meetData}
          onUpdate={updateMeetData}
          onNext={editMode ? handleComplete : nextStep}
          onBack={prevStep}
          isEditing={editMode}
        />
      )}
      
      {step === 4 && !editMode && (
        <MeetSummary
          data={meetData}
          onComplete={handleComplete}
          onBack={prevStep}
          alreadyCreated={!!sessionStorage.getItem(WIZARD_CREATED_ID_KEY)}
        />
      )}
    </div>
  );
}
